import React, { memo, useMemo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';
import { Icon } from 'tea-component';
import classNames from 'classnames';
import { IDragListProps, IQuickDragItem, ISortableEvent, ISortableEndEvent } from './types';
import { DEFAULT_CLASS_NAMES, DEFAULT_CONFIG } from './constants';
import './index.scss';

interface IDragListItemProps {
  item: IQuickDragItem;
  index: number;
  itemClassName: string;
  renderItem?: (item: IQuickDragItem, index: number) => React.ReactNode;
  onItemClick: (index: number) => void;
}

const DragListItemInner: React.FC<IDragListItemProps> = ({
  item,
  index,
  itemClassName,
  renderItem,
  onItemClick,
}) => {
  const handleClick = (): void => {
    onItemClick(index);
  };

  // Hover 态改为直接操作 DOM class，避免鼠标移动时触发 React setState -> 整列表重渲染。
  const handleMouseEnterItem = useCallback((event: React.MouseEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;
    const hoverClasses = Array.isArray(item.hoverClassName)
      ? item.hoverClassName
      : item.hoverClassName
        ? [item.hoverClassName]
        : [];
    hoverClasses.forEach((cls) => {
      if (cls) {
        target.classList.add(cls);
      }
    });
  }, [item.hoverClassName]);

  const handleMouseLeaveItem = useCallback((event: React.MouseEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;
    const hoverClasses = Array.isArray(item.hoverClassName)
      ? item.hoverClassName
      : item.hoverClassName
        ? [item.hoverClassName]
        : [];
    hoverClasses.forEach((cls) => {
      if (cls) {
        target.classList.remove(cls);
      }
    });
  }, [item.hoverClassName]);

  return (
    <div
      key={item.id}
      data-id={item.id}
      className={itemClassName}
      onClick={handleClick}
      onMouseEnter={handleMouseEnterItem}
      onMouseLeave={handleMouseLeaveItem}
    >
      {renderItem ? (
        renderItem(item, index)
      ) : (
        <>
          <Icon
            type="drop"
            className="dbl-quick-drag-list-item-icon"
          />
          <span>{item.text}</span>
        </>
      )}
    </div>
  );
};

const DragListItem = memo(
  DragListItemInner,
  // 仅当行数据/渲染函数/关键交互引用变化时才重渲染，降低父组件刷新对全部 item 的影响。
  (prevProps, nextProps) =>
    prevProps.item === nextProps.item
    && prevProps.index === nextProps.index
    && prevProps.itemClassName === nextProps.itemClassName
    && prevProps.renderItem === nextProps.renderItem
    && prevProps.onItemClick === nextProps.onItemClick,
);
DragListItem.displayName = 'DragListItem';

/**
 * 拖拽列表组件
 * 支持拖拽排序、选中高亮
 */
export const DragList: React.FC<IDragListProps> = ({
  items,
  selectedIndex,
  onItemClick,
  onDragEnd,
  height,
  maxHeight,
  className,
  style,
  renderItem,
  title,
  activeClass = DEFAULT_CLASS_NAMES.active,
  chosenClass = DEFAULT_CLASS_NAMES.chosen,
  dragClass = DEFAULT_CLASS_NAMES.drag,
  ghostClass = DEFAULT_CLASS_NAMES.ghost,
  fallbackClass,
  onChoose,
  onStart,
  onEnd,
  onUpdate,
  onAdd,
  onRemove,
  onFilter,
  onMove,
  animation = DEFAULT_CONFIG.animation,
  delay,
  handle,
  filter,
  disabled,
}) => {
  const [innerItems, setInnerItems] = useState<IQuickDragItem[]>(items);
  const latestItemsRef = useRef<IQuickDragItem[]>(items);

  /**
   * 为什么这里用 useLayoutEffect 同步 props -> innerItems？
   *
   * - 背景：父组件 `QuickDragList` 在“置顶/置底”时，会在 layout 阶段（useLayoutEffect）读取容器的 `scrollHeight`
   *   并执行 `scrollTo(...)`。而 `DragList` 真实渲染依赖的是本地 state `innerItems`，不是直接用 props `items`。
   * - 问题：如果这里使用 useEffect（发生在 paint 之后），会出现一帧窗口期：父组件已经基于“新 value”触发滚动，
   *   但子组件 DOM 仍然是“旧 innerItems”的高度，导致第一次“置底”拿到的 `scrollHeight` 偏小，滚动会差一截。
   * - 作用：改为 useLayoutEffect 后，`items` 变化时会在浏览器 paint 之前同步提交 `innerItems`，确保父组件同一轮
   *   layout 阶段滚动时，列表 DOM 高度已经是最新的，从而第一次即可精确滚到底/顶。
   *
   * 性能影响与取舍：
   * - useLayoutEffect 的 setState 会让该次更新在 paint 前完成，可能增加一次同步布局/渲染成本。
   * - 但这里的触发频率通常很低：主要发生在“置顶/置底/上下移”或“拖拽结束提交最终顺序”这种离散操作；拖拽过程的高频
   *   重排仍由 `ReactSortable setList -> setInnerItems` 在组件内部驱动，不依赖父层 props 回流。
   * - 因此相对换来的交互正确性（避免滚动时序错位）而言，该同步成本在绝大多数场景下是可控的。
   */
  useLayoutEffect(() => {
    setInnerItems(items);
    latestItemsRef.current = items;
  }, [items]);

  // 列表数据
  const displayList = useMemo(() => {
    return innerItems.map((item, index) => ({ data: item, index }));
  }, [innerItems]);

  // 处理列表更新
  const handleSetList = useCallback((newList: IQuickDragItem[]): void => {
    setInnerItems(newList);
    latestItemsRef.current = newList;
  }, []);

  // 处理选中事件
  const handleChoose = useCallback((evt: ISortableEvent): void => {
    if (evt.oldIndex !== undefined) {
      onItemClick(evt.oldIndex);
    }
    onChoose?.(evt);
  }, [onChoose, onItemClick]);

  // 处理拖拽结束事件
  const handleEnd = useCallback((evt: ISortableEndEvent): void => {
    if (evt.newIndex !== undefined) {
      onItemClick(evt.newIndex);
    }
    onDragEnd(
      latestItemsRef.current.map((item) => ({
        ...item,
        chosen: false,
        selected: false,
      })),
    );
    onEnd?.(evt);
  }, [onEnd, onItemClick, onDragEnd]);

  // 容器样式
  const containerStyle = useMemo(() => {
    const baseStyle: React.CSSProperties = {
      ...style,
    };

    if (height !== undefined) {
      baseStyle.height = typeof height === 'number' ? `${height}px` : height;
    }

    if (maxHeight !== undefined) {
      baseStyle.maxHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;
    }

    return baseStyle;
  }, [style, height, maxHeight]);

  // 获取列表项的类名
  const getItemClassName = useCallback((item: IQuickDragItem, index: number): string => {
    const isSelected = index === selectedIndex;

    return classNames(
      'dbl-quick-drag-list-item',
      {
        [activeClass]: isSelected,
      },
      item.className,
      isSelected && item.activeClassName,
    );
  }, [selectedIndex, activeClass]);

  return (
    <div
      className={classNames('dbl-quick-drag-list-container', className)}
      style={containerStyle}
    >
      {title && (
        <div className="dbl-quick-drag-list-title">{title}</div>
      )}
      <ReactSortable
        list={innerItems}
        setList={handleSetList}
        animation={animation}
        delay={delay}
        handle={handle}
        filter={filter}
        disabled={disabled}
        chosenClass={chosenClass}
        dragClass={dragClass}
        ghostClass={ghostClass}
        fallbackClass={fallbackClass}
        onChoose={handleChoose}
        onStart={onStart}
        onEnd={handleEnd}
        onUpdate={onUpdate}
        onAdd={onAdd}
        onRemove={onRemove}
        onFilter={onFilter}
        onMove={onMove}
        className="dbl-quick-drag-list-ul"
      >
        {displayList.map((item) => (
          <DragListItem
            key={item.data.id}
            item={item.data}
            index={item.index}
            itemClassName={getItemClassName(item.data, item.index)}
            renderItem={renderItem}
            onItemClick={onItemClick}
          />
        ))}
      </ReactSortable>
    </div>
  );
};

